import React, { useEffect, useState } from "react";
import { X, Lock, BarChart3, Megaphone } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getConsent, saveConsent } from "@/lib/analytics";

/**
 * Bandeau de consentement RGPD — Consent Mode v2.
 *
 * - NON-BLOQUANT : le visiteur peut naviguer, envoyer un formulaire, se connecter
 *   sans accepter les cookies Analytics ou Marketing.
 * - 3 boutons de taille et visibilité équivalentes.
 * - Aucune case pré-cochée.
 * - Réouvrable à tout moment via un événement window "ma:open-consent"
 *   (déclenché par le lien "Gérer mes cookies" du Footer).
 */
export default function CookieConsent() {
  const hideHere = useLocation().pathname.startsWith("/jeu/");
  const [visible, setVisible] = useState(false);
  const [detailed, setDetailed] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Affiche au premier passage (aucun choix stocké)
    const c = getConsent();
    if (!c) {
      setVisible(true);
    } else {
      setAnalytics(!!c.analytics);
      setMarketing(!!c.marketing);
    }
    // Réouverture depuis le footer
    const onOpen = () => {
      const cc = getConsent();
      setAnalytics(!!cc?.analytics);
      setMarketing(!!cc?.marketing);
      setDetailed(true);
      setVisible(true);
    };
    window.addEventListener("ma:open-consent", onOpen);
    return () => window.removeEventListener("ma:open-consent", onOpen);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setVisible(false);
  };
  const savePrefs = () => {
    saveConsent({ analytics, marketing });
    setVisible(false);
  };

  if (!visible || hideHere) return null;

  return (
    <div
      role="dialog"
      aria-label="Préférences cookies"
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] pointer-events-none"
    >
      <div className="max-w-[1100px] mx-auto p-4 md:p-6 pointer-events-auto">
        <div
          className="relative bg-[#0a0a0a]/95 backdrop-blur-md border border-[#DCB457]/25 shadow-2xl p-5 md:p-6"
          style={{ boxShadow: "0 -20px 60px -10px rgba(0,0,0,0.6), 0 0 0 1px rgba(220,180,87,0.15)" }}
        >
          <button
            type="button"
            onClick={rejectAll}
            aria-label="Fermer et refuser les cookies non-nécessaires"
            data-testid="cookie-close"
            className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors p-1"
          >
            <X size={16} strokeWidth={1.6} />
          </button>

          {!detailed ? (
            <>
              <div className="flex items-center gap-2 text-[#DCB457] mb-2">
                <span className="text-[10px] uppercase tracking-[0.24em]">⚙  Cookies · Confidentialité</span>
              </div>
              <p className="text-sm md:text-[13px] text-white/75 leading-relaxed max-w-3xl">
                Nous utilisons des cookies pour mesurer l'audience de notre site et améliorer votre expérience. Vous pouvez tout accepter, tout refuser, ou personnaliser vos préférences. Vous pourrez modifier votre choix à tout moment via <span className="text-[#DCB457]">« Gérer mes cookies »</span> en pied de page.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setDetailed(true)}
                  data-testid="cookie-btn-customize"
                  className="flex-1 h-11 px-5 border border-white/25 text-white hover:border-white/50 hover:bg-white/5 text-sm uppercase tracking-[0.14em] transition-colors"
                >
                  Personnaliser
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  data-testid="cookie-btn-reject"
                  className="flex-1 h-11 px-5 border border-white/25 text-white hover:border-white/50 hover:bg-white/5 text-sm uppercase tracking-[0.14em] transition-colors"
                >
                  Tout refuser
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  data-testid="cookie-btn-accept"
                  className="flex-1 h-11 px-5 bg-[#DCB457] text-black hover:bg-[#e0c26a] text-sm uppercase tracking-[0.14em] font-semibold transition-colors"
                >
                  Tout accepter
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] mb-3">
                Personnalisez vos préférences
              </p>
              <div className="space-y-4">
                <CategoryRow
                  Icon={Lock}
                  title="Nécessaires"
                  desc="Session, préférence de langue, panier, PWA. Ne peuvent pas être désactivés — indispensables au fonctionnement du site."
                  locked
                  checked
                />
                <CategoryRow
                  Icon={BarChart3}
                  title="Mesure d'audience"
                  desc={
                    <>
                      Google Analytics 4 (Google Ireland Ltd) : mesure du trafic et des conversions, IP anonymisée. Cookies : <code className="text-[#DCB457]/80">_ga</code>, <code className="text-[#DCB457]/80">_ga_DHKW8QKVPR</code> (13 mois).
                      <br />
                      PostHog / Analytics Emergent : mesure interne. Cookies/stockages : <code className="text-[#DCB457]/80">ph_phc_…</code> (12 mois).
                      <br />
                      Aucune donnée personnelle transmise.
                    </>
                  }
                  checked={analytics}
                  onChange={setAnalytics}
                  testid="cookie-toggle-analytics"
                />
                <CategoryRow
                  Icon={Megaphone}
                  title="Marketing"
                  desc="Aucun tracker publicitaire actif actuellement."
                  checked={marketing}
                  onChange={setMarketing}
                  testid="cookie-toggle-marketing"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setDetailed(false)}
                  data-testid="cookie-btn-back"
                  className="h-11 px-5 border border-white/25 text-white/80 hover:border-white/45 hover:bg-white/5 text-xs uppercase tracking-[0.14em] transition-colors"
                >
                  ← Retour
                </button>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={savePrefs}
                  data-testid="cookie-btn-save"
                  className="h-11 px-5 bg-[#DCB457] text-black hover:bg-[#e0c26a] text-sm uppercase tracking-[0.14em] font-semibold transition-colors"
                >
                  Enregistrer mes choix
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ Icon, title, desc, locked, checked, onChange, testid }) {
  return (
    <div className="flex items-start gap-4 pb-4 border-b border-white/8 last:border-b-0">
      <Icon size={18} strokeWidth={1.4} className="text-[#DCB457]/80 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-semibold text-white">{title}</p>
          {locked ? (
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#DCB457]/80 border border-[#DCB457]/40 px-2 py-1">Toujours actif</span>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={checked}
              onClick={() => onChange(!checked)}
              data-testid={testid}
              className={`relative w-11 h-6 border transition-colors ${checked ? "bg-[#DCB457] border-[#DCB457]" : "bg-transparent border-white/30"}`}
            >
              <span
                className={`absolute top-[2px] w-4 h-4 transition-all ${checked ? "left-[22px] bg-black" : "left-[2px] bg-white/80"}`}
              />
            </button>
          )}
        </div>
        <p className="text-xs text-white/60 mt-1.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
